import { Check, ChevronDown, X } from "lucide-react";
import { useEffect, useRef, useState } from "react";
import { Input } from "./shadcn/input";

// Improved Searchable Dropdown Component
interface SearchableDropdownProps {
  id: string;
  value: string;
  onChange: (value: string) => void;
  options: Array<{ value: string; label: string }>;
  placeholder: string;
  label?: string;
  required?: boolean;
  disabled?: boolean;
}

export function SearchableDropdown({
  id,
  value,
  onChange,
  options,
  placeholder,
  label,
  required = false,
  disabled = false,
}: SearchableDropdownProps) {
  const [isOpen, setIsOpen] = useState(false);
  const [search, setSearch] = useState("");
  const [highlightedIndex, setHighlightedIndex] = useState(-1);
  const dropdownRef = useRef<HTMLDivElement>(null);
  const inputRef = useRef<HTMLInputElement>(null);

  // Set the search input value when the selected value changes
  useEffect(() => {
    if (value) {
      const option = options.find((opt) => opt.value === value);
      if (option) {
        setSearch(option.label);
      }
    } else {
      setSearch("");
    }
    // Don't open dropdown on initial render or value change
    setIsOpen(false);
  }, [value, options]);

  // Find the closest match index
  const getClosestMatchIndex = () => {
    if (!search) return -1;
    const searchLower = search.toLowerCase();
    return options.findIndex((option) =>
      option.label.toLowerCase().includes(searchLower)
    );
  };

  // Update highlighted index when search changes
  useEffect(() => {
    if (search) {
      const closestMatchIndex = getClosestMatchIndex();
      setHighlightedIndex(closestMatchIndex);
    } else {
      setHighlightedIndex(-1);
    }
  }, [search]);

  // Handle clicks outside the dropdown to close it
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (
        dropdownRef.current &&
        !dropdownRef.current.contains(event.target as Node)
      ) {
        setIsOpen(false);
      }
    };

    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, []);

  // Handle keyboard navigation
  const handleKeyDown = (e: React.KeyboardEvent) => {
    switch (e.key) {
      case "ArrowDown":
        e.preventDefault();
        setIsOpen(true);
        setHighlightedIndex((prev) =>
          prev < options.length - 1 ? prev + 1 : prev
        );
        break;
      case "ArrowUp":
        e.preventDefault();
        setIsOpen(true);
        setHighlightedIndex((prev) => (prev > 0 ? prev - 1 : prev));
        break;
      case "Enter":
        e.preventDefault();
        if (isOpen && highlightedIndex >= 0) {
          handleSelect(options[highlightedIndex].value);
        }
        break;
      case "Escape":
        setIsOpen(false);
        break;
      case "Tab":
        setIsOpen(false);
        break;
    }
  };

  // Handle option selection
  const handleSelect = (selectedValue: string) => {
    const option = options.find((opt) => opt.value === selectedValue);
    if (option) {
      onChange(selectedValue);
      setSearch(option.label);
      setIsOpen(false);
      setHighlightedIndex(-1);
    }
  };

  // Clear the selected value
  const handleClear = (e: React.MouseEvent) => {
    e.stopPropagation(); // Prevent the dropdown from opening
    onChange("");
    setSearch("");
    if (inputRef.current) {
      inputRef.current.focus();
    }
  };

  // Toggle dropdown when clicking the input container
  const handleInputContainerClick = () => {
    if (!disabled) {
      setIsOpen(true); // Always open on click
      if (inputRef.current) {
        inputRef.current.focus();
      }
    }
  };

  // Handle input focus
  const handleInputFocus = () => {
    // Don't do anything on focus - this prevents auto-opening on tab
  };

  return (
    <div ref={dropdownRef} className="relative">
      <label
        htmlFor={id}
        className="text-sm font-medium text-gray-700 dark:text-gray-300"
      >
        {label}
      </label>
      <div
        className="relative mt-1 cursor-pointer"
        onClick={handleInputContainerClick}
      >
        <Input
          ref={inputRef}
          id={id}
          type="text"
          value={search}
          onChange={(e) => {
            const newValue = e.target.value;
            setSearch(newValue);
            setIsOpen(true);
          }}
          onFocus={handleInputFocus}
          onKeyDown={handleKeyDown}
          placeholder={placeholder}
          className="w-full pr-10 border-gray-300 focus-visible:ring-0 focus-visible:ring-offset-0 focus-visible:border-blue-500 focus-visible:border-2"
          required={required}
          disabled={disabled}
          autoComplete="off"
          onClick={(e) => {
            e.stopPropagation();
            handleInputContainerClick();
          }}
        />
        <div className="absolute right-3 top-1/2 -translate-y-1/2 flex items-center space-x-1">
          {value && (
            <X
              className="h-4 w-4 text-gray-400 hover:text-gray-600 cursor-pointer"
              onClick={handleClear}
            />
          )}
          <ChevronDown
            className={`h-4 w-4 text-gray-400 transition-transform ${
              isOpen ? "transform rotate-180" : ""
            }`}
            onClick={(e) => {
              e.stopPropagation();
              if (!disabled) {
                setIsOpen(!isOpen);
                if (inputRef.current) {
                  inputRef.current.focus();
                }
              }
            }}
          />
        </div>
      </div>
      {isOpen && (
        <div className="absolute z-50 w-full mt-1 bg-white rounded-md shadow-lg border border-gray-200">
          <ul
            className="py-1 max-h-60 overflow-auto rounded-md"
            role="listbox"
            tabIndex={-1}
          >
            {options.length > 0 ? (
              options.map((option, index) => {
                const isMatch =
                  search &&
                  option.label.toLowerCase().includes(search.toLowerCase());
                return (
                  <li
                    key={option.value}
                    className={`
                      px-3 py-2 cursor-pointer flex items-center justify-between
                      ${
                        highlightedIndex === index
                          ? "bg-blue-50 text-blue-600"
                          : isMatch
                          ? "bg-gray-50"
                          : "text-gray-900 hover:bg-gray-50"
                      }
                    `}
                    role="option"
                    aria-selected={value === option.value}
                    onClick={() => handleSelect(option.value)}
                  >
                    <span>{option.label}</span>
                    {value === option.value && (
                      <Check className="h-4 w-4 text-blue-600" />
                    )}
                  </li>
                );
              })
            ) : (
              <li className="px-3 py-2 text-gray-500">No options found</li>
            )}
          </ul>
        </div>
      )}
    </div>
  );
}
